import React from "react";
import { Info, Trophy, ListOrdered } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/contexts/AuthContext";
import TeamLogo from "@/components/TeamLogo";
import { usePlayoffs, useStandings } from "@/queries/standings";
import { getConfiguredSeason } from "@/lib/season";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Seo from "@/components/Seo";
import { getNativePlatform } from "@/lib/runtime";

import NativeStandingsPage from "@/components/native/NativeStandingsPage";

function FormBadge({ value }) {
  const variant =
    value === "V" ? "default" : value === "O" ? "secondary" : value === "F" ? "destructive" : "outline";

  const label = value === "V" ? "Vinst" : value === "O" ? "Oavgjort" : value === "F" ? "Förlust" : value;

  return (
    <Badge variant={variant} className="font-medium tracking-wide">
      {label[0]}
    </Badge>
  );
}

function formatMatchDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("sv-SE", {
    month: "short",
    day: "numeric",
  }).format(date);
}

function normalizeTeamName(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function findStandingsRowForTeam(teamName, standingsRows = []) {
  const normalizedTeam = normalizeTeamName(teamName);
  if (!normalizedTeam) return null;

  const exact = standingsRows.find((row) => normalizeTeamName(row?.team) === normalizedTeam);
  if (exact) return exact;

  return (
    standingsRows.find((row) => {
      const normalizedRow = normalizeTeamName(row?.team);
      return (
        normalizedTeam.startsWith(`${normalizedRow} `) ||
        normalizedTeam.endsWith(` ${normalizedRow}`) ||
        normalizedRow.startsWith(`${normalizedTeam} `) ||
        normalizedRow.endsWith(` ${normalizedTeam}`)
      );
    }) || null
  );
}

function getGroupTeams(group) {
  const seen = new Set();
  const teams = [];

  for (const match of group?.matches || []) {
    for (const team of [match?.home_team, match?.away_team]) {
      const key = normalizeTeamName(team);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      teams.push(team);
    }
  }

  return teams;
}

function getSeedLabelPrefix(stageType) {
  if (stageType === "quarterfinal_series") return "GS";
  if (stageType === "semifinal_series") return "QF";
  if (stageType === "final") return "Semifinalvinnare";
  return "Placering";
}

function getGroupSeedEntries(group, stageType, standingsRows = []) {
  const labelPrefix = getSeedLabelPrefix(stageType);

  return getGroupTeams(group)
    .map((team) => {
      const row = findStandingsRowForTeam(team, standingsRows);
      return {
        team,
        position: row?.position ?? null,
        label:
          stageType === "final"
            ? labelPrefix
            : row?.position
              ? `${labelPrefix} #${row.position}`
              : `${labelPrefix} saknas`,
      };
    })
    .sort((a, b) => {
      if (a.position == null && b.position == null) return a.team.localeCompare(b.team, "sv");
      if (a.position == null) return 1;
      if (b.position == null) return -1;
      return a.position - b.position;
    });
}

function renderMatchPair(match) {
  const hasScore = match?.home_score != null && match?.away_score != null;
  return (
    <div className="rounded-xl border border-border/80 bg-background/80 p-3 shadow-sm transition-colors">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary/80">
          {match?.leg ? `Möte ${match.leg}` : "Match"}
        </span>
        <span className="text-[11px] font-medium text-muted-foreground">{formatMatchDate(match?.date)}</span>
      </div>
      <div className="space-y-2.5">
        <div className="flex items-center justify-between gap-3 rounded-lg bg-card px-3 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <TeamLogo
              teamName={match?.home_team}
              size={28}
              className="h-7 w-7 shrink-0 object-contain"
            />
            <div className="min-w-0">
              <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Hemma</div>
              <span className="truncate text-[15px] font-semibold text-foreground">{match?.home_team || "Lag saknas"}</span>
            </div>
          </div>
          <span className="min-w-[2rem] text-right text-lg font-bold text-foreground">
            {hasScore ? match.home_score : "-"}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-lg bg-card px-3 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <TeamLogo
              teamName={match?.away_team}
              size={28}
              className="h-7 w-7 shrink-0 object-contain"
            />
            <div className="min-w-0">
              <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Borta</div>
              <span className="truncate text-[15px] font-semibold text-foreground">{match?.away_team || "Lag saknas"}</span>
            </div>
          </div>
          <span className="min-w-[2rem] text-right text-lg font-bold text-foreground">
            {hasScore ? match.away_score : "-"}
          </span>
        </div>
      </div>
    </div>
  );
}

function renderSeedEntries(group, stageType, standingsRows) {
  if (stageType === "final") return null;

  const seedEntries = getGroupSeedEntries(group, stageType, standingsRows);
  if (!seedEntries.length) return null;

  return (
    <div className="mb-3 flex flex-wrap gap-1.5 text-[10px] text-muted-foreground">
      {seedEntries.map((entry) => (
        <div key={`${group?.key || stageType}-${entry.team}`} className="inline-flex items-center gap-1 rounded-md border border-border/50 bg-background/60 px-2 py-1">
          <span className="font-medium text-foreground">{entry.team}</span>
          <span>{entry.label}</span>
        </div>
      ))}
    </div>
  );
}

const PLAYOFF_PLACEHOLDER_GROUPS = {
  quarterfinal_series: ["Kvartsfinal 1", "Kvartsfinal 2", "Kvartsfinal 3"],
  semifinal_series: ["Semifinal 1", "Semifinal 2"],
  final: ["Final"],
};

const PLAYOFF_PLACEHOLDER_COPY = {
  quarterfinal_series: {
    dateLabelLeg1: "18 Aug",
    dateLabelLeg2: "25 Aug",
    homeLabel: "Två lag från topp 6",
    awayLabel: "",
    body: "Alla sex kvartsfinallag ingår i två möten per serie. Motstånden fastställs först när grundserien är klar.",
  },
  semifinal_series: {
    dateLabelLeg1: "1 Sep",
    dateLabelLeg2: "8 Sep",
    body: "Semifinalerna seedas från kvartsfinaltabellen när kvartsfinalerna är spelade.",
  },
  final: {
    dateLabelLeg1: "15 Sep",
    dateLabelLeg2: "22 Sep",
    homeLabel: "Semifinalvinnare 1",
    awayLabel: "Semifinalvinnare 2",
    body: "Finalen spelas över två möten när båda semifinalerna är avgjorda.",
  },
};

function getPlaceholderTeams(stageType, groupLabel, matchIndex) {
  if (stageType === "quarterfinal_series") {
    return ["Två lag från topp 6", ""];
  }

  if (stageType === "semifinal_series") {
    if (groupLabel === "Semifinal 1") return ["QF-tabell #1", "QF-tabell #4"];
    if (groupLabel === "Semifinal 2") return ["QF-tabell #2", "QF-tabell #3"];
  }

  if (stageType === "final") {
    return ["SF-vinnare 1", "SF-vinnare 2"];
  }

  const copy = PLAYOFF_PLACEHOLDER_COPY[stageType] || PLAYOFF_PLACEHOLDER_COPY.final;
  return [copy.homeLabel, copy.awayLabel];
}

function renderPlaceholderMatchPair(stageType, index = 0, groupLabel) {
  const copy = PLAYOFF_PLACEHOLDER_COPY[stageType] || PLAYOFF_PLACEHOLDER_COPY.final;
  const teamLabels = getPlaceholderTeams(stageType, groupLabel, index);
  const isNeutralPlaceholder = true;
  const dateLabel = index === 0 ? copy.dateLabelLeg1 : copy.dateLabelLeg2;
  const isQuarterfinalPlaceholder = stageType === "quarterfinal_series";

  return (
    <div key={`${stageType}-placeholder-${index}`} className="rounded-xl border border-border/80 bg-background/80 p-3 shadow-sm transition-colors">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary/80">{`Möte ${index + 1}`}</span>
        <span className="text-[11px] font-medium text-muted-foreground">{dateLabel}</span>
      </div>
      {isNeutralPlaceholder ? (
        <div className="rounded-lg border border-dashed border-border/70 bg-card/70 px-3 py-3">
          {isQuarterfinalPlaceholder ? (
            <div className="text-center">
              <span className="block text-[13px] font-medium text-muted-foreground">{teamLabels[0]}</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 text-center">
              <span className="truncate text-[13px] font-medium text-muted-foreground">{teamLabels[0]}</span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary/70">mot</span>
              <span className="truncate text-[13px] font-medium text-muted-foreground">{teamLabels[1]}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2.5">
          {teamLabels.map((label, rowIndex) => (
            <div key={`${stageType}-${index}-${rowIndex}`} className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-border/70 bg-card/70 px-3 py-2.5">
              <div className="flex min-w-0 items-center gap-2">
                <div className="h-7 w-7 shrink-0 rounded-md border border-dashed border-border/70 bg-muted/40" />
                <div className="min-w-0">
                  <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {rowIndex === 0 ? "Plats 1" : "Plats 2"}
                  </div>
                  <span className="block truncate text-[13px] font-medium text-muted-foreground">{label}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function renderPlaceholderGroupCards(stageType) {
  const labels = PLAYOFF_PLACEHOLDER_GROUPS[stageType] || [];
  return labels.map((label) => (
    <div key={`${stageType}-${label}`} className="rounded-xl border border-border/70 bg-card p-3 shadow-sm relative">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">{label}</div>
        </div>
      </div>
      <div className="relative space-y-2">
        {[0, 1].map((index) => renderPlaceholderMatchPair(stageType, index, label))}
      </div>
    </div>
  ));
}

function renderPlaceholderStageIntro(stageType) {
  const copy = PLAYOFF_PLACEHOLDER_COPY[stageType];
  if (!copy?.body) return null;

  return (
    <div className="rounded-lg border border-dashed border-border/70 bg-muted/20 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
      {copy.body}
    </div>
  );
}

function renderStageStandingsCard(stage, title) {
  const rows = stage?.standings?.rows || [];
  const isPlayoffStage = stage?.stage_type && stage.stage_type !== "regular_season";
  const stageType = stage?.stage_type;
  if (!rows.length) {
    if (stageType === "quarterfinal_series") {
      return (
        <div className="rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
          Här visas de fyra lag som tar sig vidare från kvartsfinalerna.
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
        Ingen {title.toLowerCase()} ännu.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-border/70 bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
        {isPlayoffStage
          ? "Sortering: lagpoäng, sedan matchpoängkvot och därefter grundserieplacering."
          : "Sortering: poäng, skillnad och gjorda poäng."}
      </div>
      {rows.slice(0, 4).map((row) => (
        <div key={row.team_id} className="rounded-lg border border-border/80 bg-background/70 px-3 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <TeamLogo
                teamName={row.team}
                size={24}
                className="h-6 w-6 shrink-0 object-contain"
              />
              <span className="truncate text-sm font-semibold text-foreground">
                #{row.position} {row.team}
              </span>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className="shrink-0 text-sm font-bold text-foreground">
                  {row.points} {isPlayoffStage ? "lp" : "p"}
                </button>
              </PopoverTrigger>
              <PopoverContent side="bottom" align="end" sideOffset={8} className="w-64 border-border bg-background shadow-lg">
                <div className="space-y-1.5 text-left">
                  <div className="text-sm font-semibold text-foreground">
                    {isPlayoffStage ? "Lagpoäng" : "Poäng"}
                  </div>
                  <div className="text-xs leading-relaxed text-muted-foreground">
                    {isPlayoffStage
                      ? "`lp` betyder lagpoäng i tabellen. Vinst ger 2 lagpoäng, oavgjort 1 och förlust 0."
                      : "`p` är lagets tabellpoäng i grundserien. Vinst ger 2 poäng, oavgjort 1 och förlust 0. Dessutom delas 1 bonuspoäng ut till laget som har flest sammanlagda förarpoäng över hemma- och bortamötet mot samma motståndare."}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          {isPlayoffStage ? (
            <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="rounded-md bg-muted/40 px-2 py-1 text-left">
                    Matchpoäng <span className="ml-1 font-semibold text-foreground">{row.for_points ?? 0}–{row.against_points ?? 0}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent side="bottom" align="start" sideOffset={8} className="w-64 border-border bg-background shadow-lg">
                  <div className="space-y-1.5 text-left">
                    <div className="text-sm font-semibold text-foreground">Matchpoäng</div>
                    <div className="text-xs leading-relaxed text-muted-foreground">
                      Matchpoäng visar den sammanlagda poängen från lagets två möten i kvartsfinalen. Exempel: `100–80` betyder att laget totalt har kört ihop 100 poäng i sina två kvartsfinalmatcher, medan motståndarlaget i samma två matcher totalt har kört ihop 80 poäng.
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="rounded-md bg-muted/40 px-2 py-1 text-left">
                    Kvot <span className="ml-1 font-semibold text-foreground">{row.rider_quotient != null ? Number(row.rider_quotient).toFixed(3) : "-"}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent side="bottom" align="center" sideOffset={8} className="w-64 border-border bg-background shadow-lg">
                  <div className="space-y-1.5 text-left">
                    <div className="text-sm font-semibold text-foreground">Matchpoängkvot</div>
                    <div className="text-xs leading-relaxed text-muted-foreground">
                      Kvoten räknas som lagets egna matchpoäng dividerat med motståndarnas matchpoäng. Högre kvot är bättre och används som tie-break efter lagpoäng.
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="rounded-md bg-muted/40 px-2 py-1 text-left">
                    Grundserie <span className="ml-1 font-semibold text-foreground">#{row.regular_season_position ?? "-"}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent side="bottom" align="end" sideOffset={8} className="w-64 border-border bg-background shadow-lg">
                  <div className="space-y-1.5 text-left">
                    <div className="text-sm font-semibold text-foreground">Grundserieplacering</div>
                    <div className="text-xs leading-relaxed text-muted-foreground">
                      Om lagen fortfarande står lika efter lagpoäng och matchpoängkvot avgör deras placering från grundserien ordningen.
                      {stageType === "quarterfinal_series"
                        ? " Kvartsfinaltabellen omfattar alla sex kvartsfinallag, men bara topp fyra visas här."
                        : ""}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          ) : (
            <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
              <div className="rounded-md bg-muted/40 px-2 py-1">
                Poängskillnad <span className="ml-1 font-semibold text-foreground">{row.points_diff > 0 ? `+${row.points_diff}` : row.points_diff}</span>
              </div>
              <div className="rounded-md bg-muted/40 px-2 py-1">
                Körda <span className="ml-1 font-semibold text-foreground">{row.points_for}</span>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function TopOverviewSkeleton() {
  return (
    <section id="tableOverview" className="grid gap-4 sm:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <Card key={index} className="border-primary/30 bg-card/80">
          <CardContent className="p-4">
            <Skeleton className="h-4 w-20" />
            <div className="mt-2 flex items-center gap-3">
              <Skeleton className="h-9 w-12" />
              <Skeleton className="h-10 w-10 rounded-full" />
              <Skeleton className="h-6 w-32 max-w-full" />
            </div>
            <Skeleton className="mt-3 h-4 w-36 max-w-full" />
          </CardContent>
        </Card>
      ))}
    </section>
  );
}

function StandingsTableSkeleton() {
  return (
    <Card id="leagueTableCard" className="overflow-hidden">
      <CardHeader className="bg-muted/40">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <CardTitle>Tabell</CardTitle>
          </div>
          <Skeleton className="h-6 w-24 rounded-full" />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12 text-center">Pl</TableHead>
              <TableHead>Lag</TableHead>
              <TableHead className="w-16 text-center">M</TableHead>
              <TableHead className="w-16 text-center">V</TableHead>
              <TableHead className="w-16 text-center">O</TableHead>
              <TableHead className="w-16 text-center">F</TableHead>
              <TableHead className="w-16 text-center">Poäng</TableHead>
              <TableHead className="w-40 text-center">Form</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 8 }).map((_, index) => (
              <TableRow key={index}>
                <TableCell className="text-center"><Skeleton className="mx-auto h-4 w-4" /></TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <Skeleton className="h-5 w-28 max-w-full" />
                  </div>
                </TableCell>
                <TableCell className="text-center"><Skeleton className="mx-auto h-4 w-5" /></TableCell>
                <TableCell className="text-center"><Skeleton className="mx-auto h-4 w-5" /></TableCell>
                <TableCell className="text-center"><Skeleton className="mx-auto h-4 w-5" /></TableCell>
                <TableCell className="text-center"><Skeleton className="mx-auto h-4 w-5" /></TableCell>
                <TableCell className="text-center"><Skeleton className="mx-auto h-4 w-8" /></TableCell>
                <TableCell>
                  <div className="flex items-center justify-center gap-1">
                    {Array.from({ length: 5 }).map((__, badgeIndex) => (
                      <Skeleton key={badgeIndex} className="h-6 w-6 rounded-md" />
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function PlayoffProgressSkeleton() {
  return (
    <div className="w-full px-0">
      <div className="relative">
        <div className="mt-6 grid h-12 w-full grid-cols-4 items-center">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="flex items-center justify-center">
              <Skeleton className="h-12 w-12 rounded-full" />
            </div>
          ))}
        </div>
        <div className="mt-2 grid w-full grid-cols-4 text-center">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="flex items-center justify-center">
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PlayoffColumnSkeleton({ withStandings = false, showWinner = false }) {
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-border/70 bg-card p-4 shadow-sm">
        <div className="mb-3">
          <Skeleton className="h-4 w-28" />
        </div>
        {withStandings ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full rounded-md" />
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="rounded-lg border border-border/80 bg-background/70 px-3 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <Skeleton className="h-6 w-6 rounded-full" />
                    <Skeleton className="h-4 w-24 max-w-full" />
                  </div>
                  <Skeleton className="h-4 w-10" />
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  <Skeleton className="h-7 w-full rounded-md" />
                  <Skeleton className="h-7 w-full rounded-md" />
                  <Skeleton className="h-7 w-full rounded-md" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {Array.from({ length: 2 }).map((_, matchIndex) => (
              <div key={matchIndex} className="rounded-xl border border-border/80 bg-background/80 p-3 shadow-sm">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <Skeleton className="h-4 w-14" />
                  <Skeleton className="h-4 w-10" />
                </div>
                <div className="space-y-2.5">
                  {Array.from({ length: 2 }).map((__, teamIndex) => (
                    <div key={teamIndex} className="flex items-center justify-between gap-3 rounded-lg bg-card px-3 py-2.5">
                      <div className="flex min-w-0 items-center gap-2">
                        <Skeleton className="h-7 w-7 rounded-full" />
                        <div className="space-y-1">
                          <Skeleton className="h-3 w-12" />
                          <Skeleton className="h-4 w-24 max-w-full" />
                        </div>
                      </div>
                      <Skeleton className="h-6 w-6" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {showWinner ? (
        <div className="rounded-lg border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <Skeleton className="h-11 w-11 rounded-full" />
            <div className="min-w-0 space-y-2">
              <Skeleton className="h-4 w-28" />
              <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-6 w-24 max-w-full" />
              </div>
              <Skeleton className="h-3 w-36 max-w-full" />
            </div>
          </div>
          <Skeleton className="mt-4 h-8 w-full rounded-md" />
        </div>
      ) : null}
    </div>
  );
}

function PlayoffsSkeleton() {
  return (
    <div className="overflow-x-auto scroll-smooth snap-x snap-mandatory sm:snap-none sm:overflow-x-auto lg:overflow-x-hidden">
      <div className="flex w-full min-w-[400%] flex-col sm:min-w-[200%] md:min-w-[133.333%] lg:min-w-0">
        <PlayoffProgressSkeleton />
        <div className="flex items-start py-6">
          <div className="w-1/4 shrink-0 self-start px-4">
            <PlayoffColumnSkeleton />
          </div>
          <div className="w-1/4 shrink-0 self-start px-6">
            <PlayoffColumnSkeleton withStandings />
          </div>
          <div className="w-1/4 shrink-0 self-start px-6">
            <PlayoffColumnSkeleton />
          </div>
          <div className="w-1/4 shrink-0 self-start px-4">
            <PlayoffColumnSkeleton showWinner />
          </div>
        </div>
      </div>
    </div>
  );
}

const PLAYOFF_STEP_INFO = {
  "Kvartsfinal": {
    title: "Kvartsfinal",
    description:
      "Grundseriens etta väljer först motstånd bland lagen 4 till 6. Därefter väljer tvåan bland de två lag som återstår, och den tredje kvartsfinalen spelas mellan de sista två återstående lagen.",
  },
  "QF-tabell": {
    title: "Kvartsfinaltabell",
    description:
      "Kvartsfinalstabell visar de 4 lag som tagit sig vidare från kvartsfinaler baserat på, lagpoäng, matchpoängskvot och grundserieplacering.",
  },
  "Semifinal": {
    title: "Semifinal",
    description:
      "Lag 1 i kvartsfinaltabellen möter lag 4 och lag 2 möter lag 3. Semifinalerna avgörs över två möten, ett hemma och ett borta.",
  },
  "Final": {
    title: "Final",
    description:
      "De två semifinalvinnarna går till final. Finalen avgörs över två möten och den sammanlagda totalpoängen från båda matcherna utser svenska mästaren.",
  },
};

export default function TabellPage() {
  const { user } = useAuth() || {};
  const season = getConfiguredSeason();
  const playoffSteps = 4;
  const playoffScrollStateKey = React.useMemo(
    () => `tabell.playoffs.autoscroll.${season}`,
    [season]
  );
  const navigate = useNavigate();
  const { tab: tabParam } = useParams();
  const [searchParams] = useSearchParams();
  const TAB_REGULAR = "tabell";
  const TAB_PLAYOFFS = "slutspel";
  const TAB_ALIASES = React.useMemo(
    () => ({
      regular: TAB_REGULAR,
      playoffs: TAB_PLAYOFFS,
      [TAB_REGULAR]: TAB_REGULAR,
      [TAB_PLAYOFFS]: TAB_PLAYOFFS,
    }),
    []
  );
  const activeTab = TAB_ALIASES[tabParam] || TAB_REGULAR;
  const playoffHintStorageKey = React.useMemo(() => {
    const userId = user?.id || user?._id || user?.uuid || user?.email || "guest";
    return `tabell.playoff-hint:${season}:${userId}`;
  }, [season, user]);
  const [showPlayoffLineHint, setShowPlayoffLineHint] = React.useState(false);
  const playoffsScrollRef = React.useRef(null);
  const playoffColumnRefs = React.useRef([]);
  const hasAlignedPlayoffViewRef = React.useRef(false);

  const { data, isLoading, isError } = useStandings(season, {
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });

  const rows = data?.rows || [];
  const displayRows = React.useMemo(() => {
    return rows.map((row) => ({
      ...row,
      displayPosition: row?.position,
      sharedPosition: false,
    }));
  }, [rows]);

  const topOverviewRows = React.useMemo(() => {
    return displayRows.slice(0, 3);
  }, [displayRows]);
  const {
    data: playoffsData,
    isLoading: playoffsLoading,
    isError: playoffsError,
  } = usePlayoffs(season, {
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });

  const playoffStages = Array.isArray(playoffsData?.stages) ? playoffsData.stages : [];
  const qfStage = playoffStages.find((stage) => stage.stage_type === "quarterfinal_series");
  const sfStage = playoffStages.find((stage) => stage.stage_type === "semifinal_series");
  const finalStage = playoffStages.find((stage) => stage.stage_type === "final");
  const hasAnyPlayoffMatches = playoffStages.some((stage) =>
    (stage?.groups || []).some((group) => Array.isArray(group?.matches) && group.matches.length > 0)
  );
  const regularStandingsRows = rows;
  const qfStandingsRows = qfStage?.standings?.rows || [];
  const sfStandingsRows = sfStage?.standings?.rows || [];

  const completedPlayoffStep = React.useMemo(() => {
    if (!playoffStages.length) return 0;
    const qfComplete = Boolean(qfStage?.complete);
    const sfComplete = Boolean(sfStage?.complete);
    const finalComplete = Boolean(finalStage?.complete);

    if (finalComplete) return 4;
    if (sfComplete) return 3;
    if (qfComplete) return 2;
    return 0;
  }, [finalStage?.complete, playoffStages.length, qfStage?.complete, sfStage?.complete]);

  const currentPlayoffStep = React.useMemo(() => {
    const hasMatches = (stage) => (
      Array.isArray(stage?.matches) && stage.matches.length > 0
    ) || (stage?.groups || []).some((group) => (
      Array.isArray(group?.matches) && group.matches.length > 0
    ));

    if (hasMatches(finalStage)) return 3;
    if (hasMatches(sfStage)) return 2;
    if (qfStage?.complete) return 1;
    return 0;
  }, [finalStage, qfStage, sfStage]);

  const renderPlayoffProgress = () => {
    const progress = Math.min(Math.max(completedPlayoffStep, 0), playoffSteps - 1) / (playoffSteps - 1);
    const lineInset = 100 / (playoffSteps * 2);
    const lineWidth = 100 - lineInset * 2;

    const steps = [
      { step: 1, label: "Kvartsfinal" },
      { step: 2, label: "Kvartsfinaltabell" },
      { step: 3, label: "Semifinal" },
      { step: 4, label: "Final" },
    ];

    return (
      <div className="w-full px-0">
        <div className="relative">
          <div className="relative grid h-12 w-full grid-cols-4 items-center mt-6">
            <div
              className="absolute top-1/2 h-1 -translate-y-1/2 overflow-hidden rounded-full bg-border/70"
              style={{ left: `${lineInset}%`, right: `${lineInset}%` }}
            >
              {showPlayoffLineHint ? (
                <>
                  <div className="playoff-progress-flow playoff-progress-flow--1" />
                  <div className="playoff-progress-flow playoff-progress-flow--2" />
                  <div className="playoff-progress-flow playoff-progress-flow--3" />
                </>
              ) : null}
            </div>
            <div
              className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-primary transition-all duration-300"
              style={{ left: `${lineInset}%`, width: `${lineWidth * progress}%` }}
            />
            {steps.map(({ step, label }) => {
              const isComplete = completedPlayoffStep >= step;
              const roman = ["I", "II", "III", "IV"][step - 1] || String(step);
              return (
                <div key={label} className="flex items-center justify-center">
                  <div
                    className={`flex h-12 w-12 z-10 items-center justify-center rounded-full border text-sm font-semibold ${
                      isComplete ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border"
                    }`}
                  >
                    {roman}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-2 grid w-full grid-cols-4 text-center">
            {steps.map(({ label }) => {
              const info = PLAYOFF_STEP_INFO[label] || PLAYOFF_STEP_INFO[label === "Kvartsfinaltabell" ? "QF-tabell" : label];
              return (
              <div key={label} className="flex items-center justify-center gap-1">
                <span className="text-xs font-semibold uppercase text-muted-foreground">
                  {label}
                </span>
                {info ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 rounded-full text-muted-foreground hover:text-foreground"
                        aria-label={`Information om ${info.title}`}
                      >
                        <Info className="h-3.5 w-3.5" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      side="bottom"
                      align="center"
                      sideOffset={8}
                      avoidCollisions={false}
                      className="w-72 border-border bg-background shadow-lg"
                    >
                      <div className="space-y-1.5 text-left">
                        <div className="text-sm font-semibold text-foreground">{info.title}</div>
                        <div className="text-xs leading-relaxed text-muted-foreground">{info.description}</div>
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : null}
              </div>
            );})}
          </div>
        </div>
      </div>
    );
  };

  React.useEffect(() => {
    try {
      hasAlignedPlayoffViewRef.current =
        sessionStorage.getItem(playoffScrollStateKey) === "1";
    } catch {
      hasAlignedPlayoffViewRef.current = false;
    }
  }, [playoffScrollStateKey]);

  React.useEffect(() => {
    if (activeTab !== TAB_PLAYOFFS) {
      setShowPlayoffLineHint(false);
      return undefined;
    }
    if (completedPlayoffStep > 1) {
      setShowPlayoffLineHint(false);
      return undefined;
    }
    if (typeof window === "undefined") return undefined;
    if (window.innerWidth >= 640) {
      setShowPlayoffLineHint(false);
      return undefined;
    }

    const container = playoffsScrollRef.current;
    const canScrollHorizontally = Boolean(container && container.scrollWidth > container.clientWidth + 12);
    if (!canScrollHorizontally) {
      setShowPlayoffLineHint(false);
      return undefined;
    }

    try {
      if (localStorage.getItem(playoffHintStorageKey) === "seen") {
        setShowPlayoffLineHint(false);
        return undefined;
      }
      localStorage.setItem(playoffHintStorageKey, "seen");
    } catch {}

    setShowPlayoffLineHint(true);
    const timer = window.setTimeout(() => {
      setShowPlayoffLineHint(false);
    }, 4200);
    return () => window.clearTimeout(timer);
  }, [TAB_PLAYOFFS, activeTab, completedPlayoffStep, playoffHintStorageKey]);

  React.useLayoutEffect(() => {
    if (activeTab !== TAB_PLAYOFFS) return;

    let attempts = 0;
    const targetIndex = currentPlayoffStep;
    const hasAlignedBefore = hasAlignedPlayoffViewRef.current;

    const tryScroll = () => {
      const container = playoffsScrollRef.current;
      const target = playoffColumnRefs.current[targetIndex];

      if (!container || !target) {
        if (attempts < 8) {
          attempts += 1;
          setTimeout(tryScroll, 80);
        }
        return;
      }

      const nextLeft =
        target.offsetLeft - Math.max(0, (container.clientWidth - target.clientWidth) / 2);
      const previousBehavior = container.style.scrollBehavior;
      container.style.scrollBehavior = hasAlignedBefore ? "auto" : "smooth";
      container.scrollLeft = Math.max(0, nextLeft);
      container.style.scrollBehavior = previousBehavior;
      hasAlignedPlayoffViewRef.current = true;
      try {
        sessionStorage.setItem(playoffScrollStateKey, "1");
      } catch {}
    };

    const timeout = setTimeout(() => tryScroll(), hasAlignedBefore ? 0 : 80);
    return () => clearTimeout(timeout);
  }, [TAB_PLAYOFFS, activeTab, currentPlayoffStep, playoffScrollStateKey]);

  const onTabChange = React.useCallback(
    (nextTab) => {
      if (![TAB_REGULAR, TAB_PLAYOFFS].includes(nextTab) || nextTab === activeTab) return;
      const search = searchParams.toString();
      navigate(
        {
          pathname: `/tabell/${nextTab}`,
          search: search ? `?${search}` : "",
        },
        { replace: true }
      );
    },
    [TAB_PLAYOFFS, TAB_REGULAR, activeTab, navigate, searchParams]
  );

  if (getNativePlatform() === "android") {
    return (
      <>
        <Seo
          title={activeTab === TAB_PLAYOFFS ? "Slutspel och tabell" : "Tabell"}
          description={activeTab === TAB_PLAYOFFS
            ? `Följ slutspelsträd, kvartsfinaler, semifinaler och final för speedwaysäsongen ${season}.`
            : `Se aktuell tabell, form och poängställning för speedwaysäsongen ${season}.`}
          canonicalPath={activeTab === TAB_PLAYOFFS ? "/tabell/slutspel" : "/tabell/tabell"}
        />
        <NativeStandingsPage
            season={season}
            activeTab={activeTab}
            onTabChange={onTabChange}
            rows={displayRows}
            standingsLoading={isLoading}
            standingsError={isError}
            playoffStages={playoffStages}
            playoffsLoading={playoffsLoading}
            playoffsError={playoffsError}
            completedPlayoffStep={completedPlayoffStep}
            currentPlayoffStep={currentPlayoffStep}
            onNavigateTeam={(teamId) => navigate(`/serie/lag/${teamId}`)}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      <Seo
        title={activeTab === TAB_PLAYOFFS ? "Slutspel och tabell" : "Tabell"}
        description={
          activeTab === TAB_PLAYOFFS
            ? `Följ slutspelsträd, kvartsfinaler, semifinaler och final för speedwaysäsongen ${season}.`
            : `Se aktuell tabell, form och poängställning för speedwaysäsongen ${season}.`
        }
        canonicalPath={activeTab === TAB_PLAYOFFS ? "/tabell/slutspel" : "/tabell/tabell"}
      />

      <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
        <div className="sticky top-[calc(env(safe-area-inset-top)+3.75rem)] z-20 -mx-4 border-b border-border/60 bg-background/95 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/85 dark:border-border/80 dark:bg-background/90 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
            <TabsList className="grid h-auto w-full grid-cols-2 gap-0 rounded-[var(--radius)] border border-border bg-card p-0 shadow-[0_8px_24px_rgba(15,23,42,0.08)] dark:border-border/80 dark:bg-card dark:shadow-[0_10px_28px_rgba(0,0,0,0.35)]">
              <TabsTrigger
                value={TAB_REGULAR}
                className="flex h-auto min-h-[44px] w-full min-w-0 items-center justify-center gap-2 rounded-[calc(var(--radius)-2px)] px-3 py-2 text-sm whitespace-nowrap data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <ListOrdered className="h-4 w-4 shrink-0" />
                Tabell
              </TabsTrigger>
              <TabsTrigger
                value={TAB_PLAYOFFS}
                className="flex h-auto min-h-[44px] w-full min-w-0 items-center justify-center gap-2 rounded-[calc(var(--radius)-2px)] px-3 py-2 text-sm whitespace-nowrap data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <Trophy className="h-4 w-4 shrink-0" />
                Slutspel
              </TabsTrigger>
            </TabsList>
        </div>

        <TabsContent value={TAB_REGULAR} className="space-y-6">
          {isLoading ? (
            <>
              <TopOverviewSkeleton />
              <StandingsTableSkeleton />
            </>
          ) : (
            <>
              <section id="tableOverview" className="grid gap-4 sm:grid-cols-3">
                {topOverviewRows.map((team) => (
                  <button
                    key={team.team_id || team.team}
                    type="button"
                    onClick={() => {
                      if (team.team_id) navigate(`/serie/lag/${team.team_id}`);
                    }}
                    className="text-left"
                    disabled={!team.team_id}
                  >
                    <Card className="border-primary/30 bg-card/80 transition-colors hover:border-primary/50 hover:bg-card disabled:pointer-events-none">
                      <CardContent className="p-4">
                      <div className="text-xs uppercase text-muted-foreground">
                        {team.sharedPosition ? "Delad placering" : "Topplacering"}
                      </div>
                      <div className="mt-2 flex items-center gap-3">
                        <span className="text-3xl font-bold">#{team.displayPosition}</span>
                        <TeamLogo
                          teamName={team.team}
                          size={40}
                          className="h-10 w-10 shrink-0 object-contain"
                        />
                        <span className="min-w-0 truncate text-lg font-semibold">{team.team}</span>
                      </div>
                      <p className="mt-3 text-sm text-muted-foreground">
                        {team.points} poäng efter {team.played} matcher.
                      </p>
                      </CardContent>
                    </Card>
                  </button>
                ))}
              </section>

              <Card id="leagueTableCard" className="overflow-hidden">
                <CardHeader className="bg-muted/40">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-3">
                      <CardTitle>Tabell</CardTitle>
                    </div>
                    <Badge variant="outline">Säsong {season}</Badge>
                  </div>
                  {/* <CardDescription>Uppdateras automatiskt efter varje matchomgång.</CardDescription> */}
                </CardHeader>
                <CardContent className="p-0">
                  {isError ? (
                    <div className="p-4 text-sm text-red-500">Kunde inte ladda tabellen.</div>
                  ) : rows.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground">Ingen tabelldata ännu.</div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12 text-center">Pl</TableHead>
                          <TableHead>Lag</TableHead>
                          <TableHead className="w-16 text-center">M</TableHead>
                          <TableHead className="w-16 text-center">V</TableHead>
                          <TableHead className="w-16 text-center">O</TableHead>
                          <TableHead className="w-16 text-center">F</TableHead>
                          <TableHead className="w-16 text-center">+/-</TableHead>
                          <TableHead className="w-16 text-center">Poäng</TableHead>
                          <TableHead className="w-40 text-center">Form</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayRows.map((team) => (
                          <TableRow key={team.team_id || team.team}>
                            <TableCell className="text-center font-semibold">{team.displayPosition}</TableCell>
                            <TableCell className="font-medium">{team.team}</TableCell>
                            <TableCell className="text-center">{team.played}</TableCell>
                            <TableCell className="text-center">{team.wins}</TableCell>
                            <TableCell className="text-center">{team.draws}</TableCell>
                            <TableCell className="text-center">{team.losses}</TableCell>
                            <TableCell className="text-center font-medium">
                              {team.points_diff > 0 ? `+${team.points_diff}` : team.points_diff}
                            </TableCell>
                            <TableCell className="text-center font-semibold">{team.points}</TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center gap-1">
                                {(team.form || []).map((value, idx) => (
                                  <FormBadge value={value} key={`${team.team}-form-${idx}`} />
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value={TAB_PLAYOFFS}>
          <Card className="overflow-hidden">
            <CardHeader className="bg-muted/40">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-3">
                  <CardTitle>Slutspel</CardTitle>
                </div>
                <Badge variant="outline">Säsong {season}</Badge>
              </div>
              {/* <CardDescription>Slutspelsträdet fylls när slutspelsmatcher börjar spelas.</CardDescription> */}
            </CardHeader>
            <CardContent className="p-0">
              {playoffsError ? (
                <div className="p-4 text-sm text-red-500">Kunde inte ladda slutspelet.</div>
              ) : playoffsLoading ? (
                <PlayoffsSkeleton />
              ) : (
                <div
                  ref={playoffsScrollRef}
                  className="overflow-x-auto scroll-smooth snap-x snap-mandatory sm:snap-none sm:overflow-x-auto lg:overflow-x-hidden"
                >
                  <div className="flex flex-col w-full min-w-[400%] sm:min-w-[200%] md:min-w-[133.333%] lg:min-w-0">
                    {renderPlayoffProgress()}
                    <div className="flex items-start py-6">
                      <div
                        ref={(el) => (playoffColumnRefs.current[0] = el)}
                        className="shrink-0 space-y-6 snap-center snap-always relative px-4 self-start w-1/4"
                      >
                        <div className="space-y-3">
                          {!hasAnyPlayoffMatches ? renderPlaceholderStageIntro("quarterfinal_series") : null}
                          {qfStage?.groups?.length ? (
                            qfStage.groups.map((group) => (
                              <div key={group.key} className="rounded-xl border border-border/70 bg-card p-3 shadow-sm relative">
                                <>
                                <div className="mb-3 flex items-start justify-between gap-3">
                                  <div>
                                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">{group.label}</div>
                                  </div>
                                </div>
                                {renderSeedEntries(group, "quarterfinal_series", regularStandingsRows)}
                                <div className="relative space-y-2">
                                  {group.matches?.length ? (
                                    group.matches.map((match) => (
                                      <div key={match.id}>{renderMatchPair(match)}</div>
                                    ))
                                  ) : (
                                    <div className="space-y-2">
                                      {[0, 1].map((index) => renderPlaceholderMatchPair("quarterfinal_series", index))}
                                    </div>
                                  )}
                                </div>
                                </>
                              </div>
                            ))
                          ) : (
                            <div className="space-y-3">{renderPlaceholderGroupCards("quarterfinal_series")}</div>
                          )}
                        </div>
                      </div>

                      <div
                        ref={(el) => (playoffColumnRefs.current[1] = el)}
                        className="shrink-0 space-y-6 snap-center snap-always relative px-6 self-start w-1/4"
                      >
                        <div className="rounded-xl border border-border/70 bg-card p-4 shadow-sm">
                          <div className="mb-3">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">Kvartsfinaltabell</div>
                          </div>
                          <div className="relative">
                            {renderStageStandingsCard(qfStage, "kvartsfinaltabell")}
                          </div>
                        </div>
                      </div>

                      <div
                        ref={(el) => (playoffColumnRefs.current[2] = el)}
                        className="shrink-0 space-y-6 snap-center snap-always relative px-6 self-start w-1/4"
                      >
                        <div className="space-y-3">
                          {!hasAnyPlayoffMatches ? renderPlaceholderStageIntro("semifinal_series") : null}
                          {sfStage?.groups?.length ? (
                            sfStage.groups.map((group) => (
                              <div key={group.key} className="rounded-xl border border-border/70 bg-card p-4 shadow-sm">
                                <>
                                <div className="mb-3">
                                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">{group.label}</div>
                                </div>
                                {renderSeedEntries(group, "semifinal_series", qfStandingsRows)}
                                <div className="relative space-y-2">
                                  {group.matches?.length ? (
                                    group.matches.map((match) => (
                                      <div key={match.id}>{renderMatchPair(match)}</div>
                                    ))
                                  ) : (
                                    <div className="space-y-2">
                                      {[0, 1].map((index) => renderPlaceholderMatchPair("semifinal_series", index, group.label))}
                                    </div>
                                  )}
                                </div>
                                </>
                              </div>
                            ))
                          ) : (
                            <div className="space-y-3">{renderPlaceholderGroupCards("semifinal_series")}</div>
                          )}
                        </div>
                      </div>

                      <div
                        ref={(el) => (playoffColumnRefs.current[3] = el)}
                        className="shrink-0 space-y-6 snap-center snap-always relative px-4 self-start w-1/4"
                      >
                        <div className="space-y-3">
                          {!hasAnyPlayoffMatches ? renderPlaceholderStageIntro("final") : null}
                          <div className="rounded-xl border border-border/70 bg-card p-3 shadow-sm">
                            <>
                            <div className="mb-3">
                              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">Final</div>
                            </div>
                            {renderSeedEntries(finalStage?.groups?.[0], "final", sfStandingsRows)}
                            <div className="relative space-y-2">
                              {finalStage?.groups?.[0]?.matches?.length ? (
                                finalStage.groups[0].matches.map((match) => (
                                  <div key={match.id}>{renderMatchPair(match)}</div>
                                ))
                              ) : (
                                <div className="space-y-2">
                                  {[0, 1].map((index) => renderPlaceholderMatchPair("final", index))}
                                </div>
                              )}
                            </div>
                            </>
                          </div>
                          {finalStage?.groups?.[0]?.winner?.requires_resolution ? (
                            <div className="rounded-lg border border-amber-500/35 bg-amber-500/10 p-4">
                              <div className="text-sm font-semibold text-foreground">Finalen är oavgjord sammanlagt</div>
                              <div className="mt-1 text-xs text-muted-foreground">
                                Totalpoäng {finalStage.groups[0].winner.winner_score}–{finalStage.groups[0].winner.runner_up_score}. Inväntar vinnare efter skiljeheat eller officiellt beslut.
                              </div>
                            </div>
                          ) : null}
                          {finalStage?.winner?.team ? (
                            <div className="rounded-lg border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-4 shadow-sm">
                              <div className="flex items-start gap-3">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
                                  <Trophy className="h-5 w-5" />
                                </div>
                                <div className="min-w-0">
                                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary/80">
                                    {finalStage.winner.title}
                                  </div>
                                  <div className="mt-1 flex items-center gap-2">
                                    <TeamLogo
                                      teamName={finalStage.winner.team}
                                      size={32}
                                      className="h-8 w-8 shrink-0 object-contain"
                                    />
                                    <div className="text-lg font-bold text-foreground">{finalStage.winner.team}</div>
                                  </div>
                                  <div className="mt-1 text-xs text-muted-foreground">
                                    {finalStage.winner.is_tie
                                      ? `Svenska mästare efter ${finalStage.winner.resolution_type === "runoff" ? "skiljeheat" : "officiellt beslut"}.`
                                      : "Svenska mästare efter två finalmöten."}
                                  </div>
                                </div>
                              </div>
                              <div className="mt-4 rounded-md border border-border/80 bg-background/70 px-3 py-2 text-xs text-muted-foreground">
                                Totalpoäng i finalmötena: {finalStage.winner.winner_score}–{finalStage.winner.runner_up_score}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
